import React from "react";
import { Link } from "react-router-dom";
import BoxedTitle from "@/components/BoxedTitle";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/063b9062-b1d7-4f16-8d31-e465e039ed51/images/406df7120a6f61d5a26734ee207025f1cc4c38f0ee6cb74ba0fb777e8ec5e47f.png";

export default function About() {
  return (
    <main data-testid="about-page">
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-16">
        <div className="vbb-overline">
          <span className="vbb-dot" />
          About / Philosophy
        </div>
        <h1
          className="font-display uppercase text-5xl sm:text-7xl lg:text-[9rem] leading-[0.9] tracking-tighter mt-6 pl-[0.35em]"
          data-testid="about-title"
        >
          <BoxedTitle size="lg" thick>
            Outside.
          </BoxedTitle>
          <br />
          <span className="text-[#e34234]">Inside.</span>
        </h1>
      </section>

      {/* Two columns: cramped / sprawling */}
      <section
        className="vbb-border-t vbb-border-b"
        data-testid="about-twocol"
      >
        <div className="mx-auto max-w-[1400px] grid grid-cols-1 lg:grid-cols-2">
          <div className="lg:vbb-border-r p-10 lg:p-16">
            <div className="font-mono-tiny text-[#6b6b6b]">01 — Inside</div>
            <h2 className="font-display text-3xl sm:text-4xl mt-4 tracking-tight leading-tight uppercase pl-[0.35em]">
              <BoxedTitle size="md">We all</BoxedTitle> live in a box
              <br />
              of our own construction.
            </h2>
            <div className="mt-6 max-w-md space-y-5 text-[15px] leading-relaxed text-[#2a2a2a]">
              <p>
                Built from what we know. From what we believe. From what we’ve
                lived through.
              </p>
              <p>
                That box is a feature, not a flaw — it’s the lens that lets us
                see at all. Pretending we don’t live inside one is the fastest
                way to mistake a wall for the world.
              </p>
              <p>
                Good work begins by acknowledging the shape of your box.
              </p>
            </div>
          </div>

          <div className="p-10 lg:p-24 lg:py-32 vbb-border-t lg:!border-t-0">
            <div className="font-mono-tiny text-[#6b6b6b]">02 — Outside</div>
            <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl mt-4 tracking-tighter leading-[0.95] uppercase pl-[0.35em]">
              <BoxedTitle size="lg">Then —</BoxedTitle>
              <br />
              step out
              <br />
              <span className="text-[#e34234]">of it.</span>
            </h2>
            <p className="mt-10 max-w-lg text-[17px] leading-relaxed text-[#2a2a2a]">
              Outside-the-box thinking isn’t a slogan. It’s the discipline of
              noticing your defaults, questioning them, and then choosing — on
              purpose — to take the obvious-but-unattempted next step.
            </p>
            <p className="mt-6 max-w-lg text-[17px] leading-relaxed text-[#2a2a2a]">
              That’s the work we like best.
            </p>
          </div>
        </div>
      </section>

      {/* Image + caption */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 border border-[#0a0a0a] overflow-hidden aspect-[16/10]">
            <img
              src={HERO_IMG}
              alt="Floating boxes"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="lg:col-span-5">
            <div className="vbb-overline">
              <span className="vbb-dot" />
              The Name
            </div>
            <h3 className="font-display text-3xl sm:text-4xl mt-4 tracking-tight uppercase pl-[0.35em]">
              <BoxedTitle size="md">Why</BoxedTitle> “Very Big Box”?
            </h3>
            <p className="mt-6 text-[16px] leading-relaxed text-[#2a2a2a]">
              The honest answer first:{" "}
              <em>outsidethebox.com was already taken.</em> The better answer
              followed: a <strong>very big box</strong> is roomy enough to hold
              a lot of ideas, and big enough that the walls aren’t in the way.
              You can still step outside it — but you have a lot more space
              before you have to.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              <Link
                to="/services"
                className="vbb-btn"
                data-testid="about-services-link"
              >
                Our services →
              </Link>
              <Link
                to="/history"
                className="vbb-btn vbb-btn-ghost"
                data-testid="about-history-link"
              >
                Founder history
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Principles grid */}
      <section
        className="vbb-border-t vbb-border-b"
        data-testid="about-principles"
      >
        <div className="mx-auto max-w-[1400px] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
          {[
            {
              n: "P/01",
              t: "Name the box",
              d: "Before redesigning the wheel, draw the wheel you’re holding.",
            },
            {
              n: "P/02",
              t: "Small bets, real ships",
              d: "We’d rather ship a small honest thing than promise a big vague one.",
            },
            {
              n: "P/03",
              t: "AI as a tool, not a stunt",
              d: "Modern AI shortens timelines. We use it where it actually earns its keep.",
            },
            {
              n: "P/04",
              t: "Founders stay founders",
              d: "We co-build. We don’t take over. You leave with a product that’s yours.",
            },
          ].map((p, i) => (
            <div
              key={p.n}
              className={`p-10 lg:p-12 ${
                i % 4 !== 3 ? "md:vbb-border-r" : ""
              } ${i >= 2 ? "md:vbb-border-t lg:!border-t-0" : ""} ${
                i === 1 ? "vbb-border-t md:!border-t-0" : ""
              }`}
              data-testid={`principle-${p.n}`}
            >
              <div className="font-mono-tiny text-[#6b6b6b]">{p.n}</div>
              <h4 className="font-display text-xl mt-4 uppercase tracking-tight pl-[0.35em]">
                <BoxedTitle size="sm">{p.t}</BoxedTitle>
              </h4>
              <p className="mt-3 text-[14px] leading-relaxed text-[#3a3a3a]">
                {p.d}
              </p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
